
import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const PrivacyPolicy = () => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  return (
    <div className="min-h-screen bg-background py-12">
      <Helmet>
        <title>Privacy Policy | DesistIt!</title>
        <meta name="description" content="Review our privacy policy to understand how DesistIt! collects, uses, and protects your personal information when using our cease and desist letter services." />
        <link rel="canonical" href="https://desistit.com/privacy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy",
            "description": "DesistIt! privacy policy describing how we handle your personal information.",
            "publisher": {
              "@type": "Organization",
              "name": "DesistIt!",
              "logo": "https://www.poweredbyeducation.org/wp-content/uploads/2025/02/DESIST2.png"
            },
            "datePublished": today,
            "dateModified": today
          })}
        </script>
      </Helmet>
      
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-5xl font-serif font-bold mb-8 text-center">Privacy Policy</h1>
        
        <div className="text-center text-gray-600 mb-12">
          <p>Updated {today} | Published {today}</p>
          <p className="mt-4">By DesistIt! staff</p>
        </div>
        
        <div className="prose prose-lg max-w-none text-left">
          <p className="text-xl">Welcome to DesistIt! ("Company," "we," "us," or "our"). Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, desistit.com (the "Website"), and use our services (the "Services"). By using our Services, you consent to the practices described in this Privacy Policy.</p>
          
          <h2>1. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          <ul>
            <li><strong>Personal Information:</strong> Name, email address, payment information, and other details you provide when using our Services.</li>
            <li><strong>Non-Personal Information:</strong> Browser type, IP address, device information, and usage data collected through cookies and analytics tools.</li>
          </ul>
          
          <h2>2. How We Use Your Information</h2>
          <p>We use your information for the following purposes:</p>
          <ul>
            <li>To provide and improve our Services.</li>
            <li>To process transactions and send service-related communications.</li>
            <li>To comply with legal requirements and prevent fraudulent activities.</li>
            <li>To analyze usage trends and enhance user experience.</li>
          </ul>
          
          <h2>3. Sharing Your Information</h2>
          <p>We do not sell or rent your personal information. However, we may share your information with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Third-party companies that assist in operating our Website and Services (e.g., payment processors, hosting providers).</li>
            <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, sale, or transfer of assets, your information may be included in the transaction.</li>
          </ul>
          
          <h2>4. Cookies & Tracking Technologies</h2>
          <p>We use cookies and similar tracking technologies to enhance your experience and analyze usage patterns. You can control cookie settings through your browser.</p>
          
          <h2>5. Data Security</h2>
          <p>We implement appropriate security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no data transmission over the internet is 100% secure.</p>
          
          <h2>6. Your Rights & Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access, correct, or delete your personal information.</li>
            <li>Opt out of marketing communications.</li>
            <li>Restrict certain data processing activities.</li>
          </ul>
          <p>To exercise these rights, please <Link to="/#contact" className="text-[#29b554] hover:underline">contact us</Link>.</p>
          
          <h2>7. Third-Party Links</h2>
          <p>Our Website may contain links to third-party websites. We are not responsible for the privacy practices of those websites.</p>
          
          <h2>8. Children's Privacy</h2>
          <p>Our Services are not intended for individuals under 18. We do not knowingly collect personal information from children.</p>
          
          <h2>9. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. Continued use of our Services after changes signifies your acceptance of the revised policy.</p>
          
          <h2>10. Contact Information</h2>
          <p>For any questions regarding this Privacy Policy, please <Link to="/" className="text-[#29b554] hover:underline">contact us</Link>.</p>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
